import { LenderAppraisal } from "@js/DTO/Messages/MakeReferralMessage.cs.d";
import { PageWithTotalCountDTO } from "@js/DTO/PageWithTotalCountDTO.cs.d";
import { BaseService } from "@js/filesfromccqbase/BaseService";
import { BroadcastService } from "@js/filesfromccqbase/BroadcastService";
import { OrganisationService } from "./OrganisationService";
import { RoleService } from "./RoleService";
import {
  AppPackagePricing,
  ApplicationUser,
  LicenseMasterStatusEnum,
  ProductTypeEnum,
  UserRoleEnum,
  UserSimpleDTO,
  Organisation,
  DealLender,
  GetCurrentUserPermissionForProductSearchResponse,
  RegisterUserAndSaveShortListedResultsRequest,
  UserClient,
  ApplicationUserCrudClient,
} from "@src/apis/ModularClient";
import { IndexService } from "@js/services/IndexService";
import { ODataHelper } from "@js/helpers/ODataHelper";
import { Expand } from "@ccq/ts-lib";
import { IPromise } from "angular";

export class UserService extends BaseService<ApplicationUser, string> {
  fileSelected: string;
  userId: number = null;

  static $inject = [
    "$http",
    "$cookies",
    "$rootScope",
    "$q",
    "$timeout",
    "BroadcastService",
    "OrganisationService",
    "RoleService",
    "IndexService",
    "ODataHelper",
    "ApplicationUserCrudClient",
  ];

  constructor(
    http: ng.IHttpService,
    cookies: ng.cookies.ICookiesService,
    rootScope: ng.IRootScopeService,
    q: ng.IQService,
    timeout: ng.ITimeoutService,
    broadcastservice: BroadcastService,
    private organisationService: OrganisationService,
    private roleService: RoleService,
    private indexService: IndexService,
    private odataHelper: ODataHelper,
    private applicationUserCrudClient: ApplicationUserCrudClient,
  ) {
    super(http, cookies, rootScope, q, timeout, broadcastservice);
    this.$baseAddress = "/api/users";
    this.$broadcastBusinessNameSingular = "User";
    this.$broadcastBusinessNamePlural = this.$broadcastBusinessNameSingular + "s";
    this.$broadcastScreen = this.$broadcastBusinessNamePlural;
  }

  setUserId(id: number) {
    this.userId = id;
  }

  getUserId() {
    return this.userId;
  }

  /**Gets the default broker organisation DTO for the current user */
  GetDefaultBrokerOrganisation(): IPromise<Expand<Organisation> | null> {
    return this.indexService.currentUserId.then((userId) =>
      this.odataHelper
        .getOne(this.applicationUserCrudClient, {
          filter: {
            id: { eq: userId },
          },
          expand: {
            defaultBrokerOrganisation: true,
          },
          select: {
            defaultBrokerOrganisation: true,
          },
        })
        .then((res) => res.defaultBrokerOrganisation),
    );
  }

  /**Gets the user's organisation DTO for the current user */
  GetUserOrganisation(): ng.IPromise<Organisation> {
    var userOrgId: number;

    if ((this.$rootScope as any).currentUser) {
      var currentUser: ApplicationUser = (this.$rootScope as any).currentUser;
      userOrgId = currentUser.organisationId;
    } else {
      this.indexService.currentUser.then((response) => {
        (this.$rootScope as any).currentUser = response;
        var currentUser: ApplicationUser = (this.$rootScope as any).currentUser;
        userOrgId = currentUser.organisationId;
      });
    }

    let defer = this.$q.defer<Organisation>();

    this.organisationService.fetch(userOrgId).then((response: Organisation) => {
      defer.resolve(response);
    });

    return defer.promise;
  }

  /** Is the user's organisation or the organisation who referred them a "white labelled" org */
  IsWhiteLabelled(): ng.IPromise<boolean> {
    let defer = this.$q.defer<boolean>();
    var userOrgId: number;
    var userReferredOrgId: number;

    if ((this.$rootScope as any).currentUser) {
      var currentUser: ApplicationUser = (this.$rootScope as any).currentUser;
      userOrgId = currentUser.organisationId;
      userReferredOrgId = currentUser.organisationReferralId;

      if (userOrgId || userReferredOrgId) {
        this.isUserOrgWhiteLabelled(userOrgId, userReferredOrgId).then((isOrgWhiteLabelled: boolean) => {
          defer.resolve(isOrgWhiteLabelled);
        });
      } else {
        defer.resolve(false);
      }
    } else {
      this.indexService.currentUser.then((response) => {
        (this.$rootScope as any).currentUser = response;
        var currentUser: ApplicationUser = (this.$rootScope as any).currentUser;
        userOrgId = currentUser.organisationId;
        userReferredOrgId = currentUser.organisationReferralId;

        if (userOrgId || userReferredOrgId) {
          this.isUserOrgWhiteLabelled(userOrgId, userReferredOrgId).then((isOrgWhiteLabelled: boolean) => {
            defer.resolve(isOrgWhiteLabelled);
          });
        } else {
          defer.resolve(false);
        }
      });
    }

    if (userOrgId || userReferredOrgId) {
    } else {
    }

    return defer.promise;
  }

  /**
   * Is the user's organisation or the organisation who referred them a "white labelled" organisation
   * @param orgId
   * @param referralOrgId
   */
  private isUserOrgWhiteLabelled(orgId: number, referralOrgId: number): ng.IPromise<boolean> {
    let defer = this.$q.defer<boolean>();

    // TODO JLH Can a user have an org ID AND a referralOrgId and in this scenario would we need to check both organisations for white labelling?
    if (orgId || referralOrgId) {
      this.organisationService.isOrgWhiteLabelled(orgId ? orgId : referralOrgId).then((isOrgWhiteLabelled: boolean) => {
        defer.resolve(isOrgWhiteLabelled);
      });
    } else {
      defer.resolve(false);
    }

    return defer.promise;
  }

  /** get the broker organisation for the current user */
  getBrokerOrganisationId(): IPromise<number> {
    return this.indexService.currentUserId.then((userId) =>
      this.odataHelper
        .getOne(this.applicationUserCrudClient, {
          filter: {
            id: { eq: userId },
          },
          select: {
            organisationId: true,
            organisationReferralId: true,
          },
        })
        .then((res) => {
          return res.organisationId ?? res.organisationReferralId;
        }),
    );
  }

  clearSelectedNavMenuAndFilterStorageData() {
    sessionStorage.removeItem("selectedNavMenu");
    sessionStorage.removeItem("clientCaseNameFilter");
    sessionStorage.removeItem("statusFilter");
  }

  unSubscribeFromEmail(emailCode: string, userId: string) {
    let defer = this.$q.defer<boolean>();
    this.$http
      .get(this.$baseAddress + "/unsubscribefromemail?emailCode=" + emailCode + "&userId=" + userId)
      .then((response) => {
        defer.resolve(response.data as boolean);
      })
      .catch((response) => {
        defer.reject(response);
      });
    return defer.promise;
  }
}
